import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { EllipsisVerticalIcon, PlusIcon } from '@heroicons/react/24/outline';
import {
  addToast,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
} from '@heroui/react';

import { useTeamMembers, useUpdateTeamMemberRole } from '~/hooks/useTeamMembers';
import { useTeamPermission } from '~/hooks/useTeamPermission';
import { getDisplayName } from '~/utils/member';

import { Feedback } from '~/components/Feedback';
import { UsersIcon } from '~/components/Icons';
import { Skeleton } from '~/components/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '~/components/Table';
import { Tooltip } from '~/components/Tooltip';

import type { Team, TeamMember, TeamRole } from '~/types';

import { AddTeamMemberModal } from './AddTeamMemberModal';
import { RemoveTeamMemberConfirmation } from './RemoveTeamMemberConfirmation';
import { TeamRoleBadge } from './TeamRoleBadge';

interface Column {
  key: string;
  label: string;
  className?: string;
}

interface TeamMembersProps {
  team: Team;
}

/**
 * Format date for display using Intl.DateTimeFormat.
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString));
}

/**
 * Check if member is the last team admin.
 * Used to prevent removal/demotion of the last admin.
 */
function isLastTeamAdmin(member: TeamMember, allMembers: TeamMember[]): boolean {
  if (member.role !== 'team_admin') return false;
  const adminCount = allMembers.filter(m => m.role === 'team_admin').length;
  return adminCount === 1;
}

export function TeamMembers({ team }: TeamMembersProps): JSX.Element {
  const { isLoading, members, total } = useTeamMembers(team.id);
  const { canManageTeamMembers, loading: permissionLoading } = useTeamPermission(team.id);
  const updateRole = useUpdateTeamMemberRole();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [removeMember, setRemoveMember] = useState<TeamMember | null>(null);

  const showAddButton = canManageTeamMembers && !permissionLoading;
  const loading = isLoading || permissionLoading;

  const columns: Column[] = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'joined', label: 'Joined' },
    { key: 'actions', label: '', className: 'text-end w-14' },
  ];

  const handleRoleChange = (member: TeamMember, newRole: TeamRole): void => {
    if (newRole === member.role) return;

    // Check if demoting last admin
    if (
      member.role === 'team_admin' &&
      newRole === 'team_member' &&
      isLastTeamAdmin(member, members)
    ) {
      addToast({
        description: 'Cannot demote the last team admin',
        color: 'danger',
      });
      return;
    }

    updateRole.mutate(
      { teamId: team.id, userId: member.user_id, role: newRole },
      {
        onSuccess: () => {
          addToast({
            description: `${getDisplayName(member)}'s role changed to ${newRole === 'team_admin' ? 'Team Admin' : 'Team Member'}`,
            color: 'success',
          });
        },
        onError: (error: Error) => {
          addToast({
            description: error.message || 'Failed to change role',
            color: 'danger',
          });
        },
      },
    );
  };

  const renderCell = (member: TeamMember, columnKey: string): ReactNode => {
    switch (columnKey) {
      case 'name':
        return <span className="font-medium text-foreground">{getDisplayName(member)}</span>;
      case 'email':
        return member.email || '-';
      case 'role':
        return <TeamRoleBadge role={member.role} />;
      case 'joined':
        return formatDate(member.joined_at);
      case 'actions':
        return renderActions(member);
      default:
        return null;
    }
  };

  const renderContent = (): JSX.Element => {
    if (loading) {
      return <Skeleton type="list" items={3} />;
    }

    if (!members.length) {
      return (
        <Feedback
          description="No members to display."
          icon={<UsersIcon className="size-16" />}
          title={null}
          type="empty"
        />
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table removeWrapper className="w-full" data-testid="team-member-list-table">
          <TableHeader columns={columns}>
            {({ className, key, label }) => (
              <TableColumn key={key} className={className}>
                {label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody items={members}>
            {member => (
              <TableRow key={member.user_id} data-testid={`team-member-row-${member.user_id}`}>
                {columns.map(column => (
                  <TableCell key={column.key}>{renderCell(member, column.key)}</TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderActions = (member: TeamMember): JSX.Element => {
    if (!canManageTeamMembers) {
      return (
        <Tooltip content="You do not have permission to manage team members">
          <Button
            isIconOnly
            variant="light"
            size="sm"
            aria-label="Team member actions"
            data-testid={`team-member-actions-${member.user_id}`}
          >
            <EllipsisVerticalIcon className="size-5" />
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <Dropdown placement="bottom-end">
          <DropdownTrigger>
            <Button
              isIconOnly
              variant="light"
              size="sm"
              aria-label="Team member actions"
              data-testid={`team-member-actions-${member.user_id}`}
            >
              <EllipsisVerticalIcon className="size-5" />
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Team member actions"
            disabledKeys={[
              // Disable current role
              member.role === 'team_admin' ? 'role-team_admin' : '',
              member.role === 'team_member' ? 'role-team_member' : '',
              // Disable demotion if last admin
              isLastTeamAdmin(member, members) ? 'remove' : '',
            ].filter(Boolean)}
          >
            <DropdownSection title="Change Role" showDivider>
              <DropdownItem
                key="role-team_admin"
                description="Can manage team members"
                onPress={() => handleRoleChange(member, 'team_admin')}
              >
                Team Admin
              </DropdownItem>
              <DropdownItem
                key="role-team_member"
                description="Standard team access"
                onPress={() => handleRoleChange(member, 'team_member')}
              >
                Team Member
              </DropdownItem>
            </DropdownSection>

            <DropdownItem
              key="remove"
              color="danger"
              className="text-danger"
              onPress={() => setRemoveMember(member)}
            >
              Remove from Team
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      );
    }
  };

  return (
    <div className="space-y-8" data-testid="TeamMembers">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Team Members</h3>
          <p className="text-sm">
            {total} member{total !== 1 ? 's' : ''} in this team
          </p>
        </div>
        {showAddButton && (
          <Button
            color="primary"
            startContent={<PlusIcon className="w-4 h-4" />}
            onPress={() => setIsAddModalOpen(true)}
            data-testid="add-team-member-button"
          >
            Add Member
          </Button>
        )}
      </div>

      {renderContent()}

      {removeMember && (
        <RemoveTeamMemberConfirmation
          isOpen={!!removeMember}
          onClose={() => setRemoveMember(null)}
          member={removeMember}
          teamId={team.id}
          teamName={team.name}
        />
      )}

      <AddTeamMemberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        teamId={team.id}
        teamName={team.name}
        orgId={team.organization_id}
        currentTeamMembers={members}
      />
    </div>
  );
}
